import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getMe, refreshTokens } from '../api/auth';

const TOKEN_KEY         = 'feuteuristix:token';
const REFRESH_TOKEN_KEY = 'feuteuristix:refreshToken';
const AuthContext       = createContext(null);

function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000; // ms
  } catch { return 0; }
}

function isExpiredOrClose(token, bufferMs = 60_000) {
  return Date.now() >= getTokenExpiry(token) - bufferMs;
}

export function AuthProvider({ children }) {
  const [user,  setUser]  = useState(null);
  const [ready, setReady] = useState(false);
  const refreshTimer      = useRef(null);

  function scheduleRefresh(token, rToken) {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    const expiry  = getTokenExpiry(token);
    const delay   = expiry - Date.now() - 60_000; // refresh 1 min before expiry
    if (delay <= 0) return;
    refreshTimer.current = setTimeout(async () => {
      try {
        const { token: newToken, refreshToken: newRefresh } = await refreshTokens(rToken);
        localStorage.setItem(TOKEN_KEY, newToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, newRefresh);
        setUser((prev) => prev ? { ...prev, token: newToken } : prev);
        scheduleRefresh(newToken, newRefresh);
      } catch {
        // Refresh failed — clear session
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        setUser(null);
      }
    }, delay);
  }

  useEffect(() => {
    const token  = localStorage.getItem(TOKEN_KEY);
    const rToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!token) { setReady(true); return; }

    const init = async () => {
      try {
        let activeToken = token;
        let activeRefresh = rToken;

        // If access token is expired/close, try to refresh before getMe
        if (isExpiredOrClose(token) && rToken) {
          const { token: newToken, refreshToken: newRefresh } = await refreshTokens(rToken);
          activeToken   = newToken;
          activeRefresh = newRefresh;
          localStorage.setItem(TOKEN_KEY, newToken);
          localStorage.setItem(REFRESH_TOKEN_KEY, newRefresh);
        }

        const u = await getMe(activeToken);
        setUser({ ...u, token: activeToken });
        scheduleRefresh(activeToken, activeRefresh);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      } finally {
        setReady(true);
      }
    };
    init();

    return () => { if (refreshTimer.current) clearTimeout(refreshTimer.current); };
  }, []); // eslint-disable-line

  const value = useMemo(() => ({
    user,
    isAuthenticated: Boolean(user),
    ready,
    login: ({ token, refreshToken, user: u }) => {
      localStorage.setItem(TOKEN_KEY, token);
      if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      setUser({ ...u, token });
      scheduleRefresh(token, refreshToken);
    },
    logout: () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      setUser(null);
    },
    // Merges a partial patch into the local user object without a round
    // trip to /me — used right after a simulation run to reflect the fresh
    // quota (plan, freeSimCreditsUsed) that createSimulation's response
    // already carries, instead of showing stale numbers until next login.
    updateUser: (patch) => {
      setUser((prev) => prev ? { ...prev, ...patch } : prev);
    },
  }), [user, ready]); // eslint-disable-line

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider />');
  return ctx;
}
